import { Link, LinkProps } from '@uhg-abyss/web/ui/Link';
import isNil from 'lodash/isNil';
import { ReactNode, isValidElement } from 'react';

import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { CustomAttributesBlock } from '../Utils/adobeTrackUtils/adobeTrackUtils';

type AnalyticsInfo = {
  destinationUrl?: string | null;
  location: string;
  name?: string | null;
  type?: string | null;
  customAttributesBlock?: CustomAttributesBlock | null;
};

/**
 * If the node is an icon element (e.g. `<IconSymbol icon=... variant=... />`),
 * return its props so they can be passed to the Link `afterIcon`/`beforeIcon`
 * (which accept the `{ icon, variant }` object form).
 */
const getLinkIcon = (node: ReactNode): LinkProps['afterIcon'] | undefined =>
  isValidElement<Extract<LinkProps['afterIcon'], object>>(node)
    ? node.props
    : undefined;

type Props = {
  after?: ReactNode;
  before?: ReactNode;
  analyticsInfo?: AnalyticsInfo;
  'aria-label'?: string;
  children?: ReactNode;
  css?: any;
  'data-testid': string;
  'data-auto-testid': string;
  fontWeight?: 'normal' | 'medium' | 'bold';
  hideIcon?: boolean;
  href?: string;
  isStandardAnchor?: boolean;
  onClick?: Function;
  size?: '$xs' | '$sm' | '$md' | '$lg';
  variant?: 'default' | 'underlined' | 'alt' | 'alt-underlined';
  isDisabled?: boolean;
  openNewWindow?: boolean;
};

export const LinkWithTracking = ({
  after,
  before,
  analyticsInfo,
  'aria-label': ariaLabel = '',
  children,
  css,
  'data-testid': testId = '',
  'data-auto-testid': dataAutoTestId = '',
  fontWeight,
  hideIcon = false,
  href = undefined,
  isStandardAnchor,
  onClick = () => {},
  size,
  variant,
  isDisabled = false,
  openNewWindow,
}: Props) => {
  const onClickWithTracking = (e) => {
    if (!isNil(onClick)) onClick(e);
    if (analyticsInfo?.location === 'body:Browse for care') return;
    adobeLinkTrackEvent({
      name: analyticsInfo?.name || testId,
      location: analyticsInfo?.location || testId,
      type: analyticsInfo?.type,
      destinationUrl: analyticsInfo?.destinationUrl || href || null,
      customAttributesBlock: analyticsInfo?.customAttributesBlock,
    });
  };
  const afterIcon = getLinkIcon(after);
  const beforeIcon = getLinkIcon(before);

  return (
    <Link
      afterIcon={afterIcon}
      aria-disabled={isDisabled}
      aria-label={ariaLabel}
      beforeIcon={beforeIcon}
      css={css}
      data-auto-testid={dataAutoTestId}
      data-testid={testId}
      fontWeight={fontWeight}
      hideIcon={hideIcon}
      href={href}
      isStandardAnchor={isStandardAnchor}
      onClick={onClickWithTracking}
      openNewWindow={openNewWindow}
      size={size}
      variant={variant}
    >
      {children}
    </Link>
  );
};
